import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, X } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';

const RING_SIZE = 96;
const STROKE = 6;
const RADIUS = (RING_SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const CalendarioCierreWidget = () => {
    const [cierre, setCierre] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const today = new Date().toDateString();
        if (localStorage.getItem(`dismiss_cierre_${today}`)) setDismissed(true);
    }, []);

    useEffect(() => {
        axiosInstance.get('/api/calendario-cierre/mis-pendientes')
            .then(res => { if (res.data.success && res.data.cierre) setCierre(res.data.cierre); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const calcCountdown = useCallback(() => {
        if (!cierre?.fecha) return null;
        const target = new Date(cierre.fecha);
        target.setHours(23, 59, 59);
        const diff = target - new Date();
        if (diff <= 0) return { expired: true, days: 0, hours: 0, minutes: 0 };
        return {
            expired: false,
            days: Math.floor(diff / 86400000),
            hours: Math.floor((diff % 86400000) / 3600000),
            minutes: Math.floor((diff % 3600000) / 60000),
        };
    }, [cierre]);

    useEffect(() => {
        if (!cierre) return;
        const tick = () => setCountdown(calcCountdown());
        tick();
        const id = setInterval(tick, 60000);
        return () => clearInterval(id);
    }, [cierre, calcCountdown]);

    if (loading || !cierre || dismissed) return null;

    const fechaLimite = cierre.fecha ? new Date(cierre.fecha) : null;
    const dias = fechaLimite ? Math.ceil((fechaLimite - new Date()) / 86400000) : null;

    // Ring progress: 15-day window, ring depletes as deadline approaches
    const maxDays = 15;
    const pct = dias !== null ? Math.max(0, Math.min(1, dias / maxDays)) : 1;
    const offset = CIRCUMFERENCE * (1 - pct);

    // Color by urgency
    const isUrgent = dias !== null && dias <= 1;
    const isWarning = dias !== null && dias <= 3 && dias > 1;
    const ringColor = isUrgent ? '#ef4444' : isWarning ? '#f59e0b' : '#0ea5e9';
    const ringTrack = isUrgent ? '#fecaca' : isWarning ? '#fef3c7' : '#e0f2fe';

    const handleDismiss = () => {
        localStorage.setItem(`dismiss_cierre_${new Date().toDateString()}`, '1');
        setDismissed(true);
    };

    return (
        <section className="relative bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden group">
            <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 z-10 w-6 h-6 rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 hover:bg-gray-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                title="Ocultar por hoy"
            >
                <X size={13} />
            </button>

            <div className="p-5">
                <div className="flex items-center gap-5">
                    {/* Ring countdown */}
                    <div className="relative shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
                        <svg width={RING_SIZE} height={RING_SIZE} className="-rotate-90">
                            <circle
                                cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RADIUS}
                                fill="none" stroke={ringTrack} strokeWidth={STROKE}
                            />
                            <circle
                                cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RADIUS}
                                fill="none" stroke={ringColor} strokeWidth={STROKE}
                                strokeLinecap="round"
                                strokeDasharray={CIRCUMFERENCE}
                                strokeDashoffset={offset}
                                className="transition-all duration-1000 ease-out"
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            {countdown && !countdown.expired ? (
                                <>
                                    <span className="text-xl font-bold text-gray-900 leading-none tabular-nums">
                                        {countdown.days > 0 ? countdown.days : countdown.hours}
                                    </span>
                                    <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                                        {countdown.days > 0 ? 'dias' : 'horas'}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="text-lg font-bold text-red-600 leading-none">!</span>
                                    <span className="text-[10px] font-medium text-red-500">Vencido</span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Cierre pendiente</p>
                        <p className="text-sm font-bold text-gray-900 leading-snug line-clamp-2">{cierre.descripcion}</p>
                        {cierre.departamento && (
                            <p className="text-xs text-gray-500 mt-1">{cierre.departamento}</p>
                        )}
                        {fechaLimite && (
                            <p className="text-xs text-gray-400 mt-1.5">
                                Entrega: {fechaLimite.toLocaleDateString('es-VE', { day: 'numeric', month: 'short' })}
                                {countdown && !countdown.expired && countdown.days > 0 && (
                                    <span className="ml-1.5 font-medium" style={{ color: ringColor }}>
                                        {countdown.days}d {countdown.hours}h
                                    </span>
                                )}
                                {countdown && !countdown.expired && countdown.days === 0 && (
                                    <span className="ml-1.5 font-medium" style={{ color: ringColor }}>
                                        {countdown.hours}h {countdown.minutes}m
                                    </span>
                                )}
                            </p>
                        )}
                    </div>
                </div>

                {/* Action */}
                <button
                    onClick={() => navigate('/calendario-cierre')}
                    className="mt-4 w-full py-2.5 rounded-xl bg-gray-900 text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
                >
                    Ver calendario
                    <ArrowRight size={14} />
                </button>
            </div>
        </section>
    );
};

export default CalendarioCierreWidget;
